import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useEnvironment } from '@/contexts/EnvironmentContext';
import { setBaseUrl, setToken, clearToken, getHttpClient } from '@/services/httpClient';

export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
}

interface AuthContextValue {
  admin: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string, apiUrl: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { current } = useEnvironment();
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const apiUrlRef = useRef(current.apiUrl);

  // Restore session on mount
  useEffect(() => {
    let cancelled = false;
    setBaseUrl(current.apiUrl);
    apiUrlRef.current = current.apiUrl;

    async function checkSession() {
      try {
        const storedToken = sessionStorage.getItem('admin_token');
        if (storedToken) {
          setToken(storedToken);
          const response = await getHttpClient().get('/api/admin/me');
          if (!cancelled) {
            setAdmin({
              id: response.data.id,
              email: response.data.email,
              displayName: response.data.displayName,
            });
          }
          return;
        }
        if (!cancelled) setAdmin(null);
      } catch {
        // Token expired or invalid — clear it
        sessionStorage.removeItem('admin_token');
        clearToken();
        if (!cancelled) setAdmin(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    checkSession();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email: string, password: string, apiUrl: string) => {
    setIsLoading(true);
    setError(null);
    try {
      setBaseUrl(apiUrl);
      apiUrlRef.current = apiUrl;

      const response = await getHttpClient().post('/api/admin/login', { email, password });
      const data = response.data;

      setToken(data.accessToken);
      sessionStorage.setItem('admin_token', data.accessToken);
      setAdmin(data.admin);
    } catch (err) {
      let message = 'Login failed';
      if (err && typeof err === 'object' && 'response' in err) {
        const resp = (err as { response?: { data?: { error?: string } } }).response;
        message = resp?.data?.error || message;
      }
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await getHttpClient().post('/api/admin/logout');
    } catch {
      // Clear state even if the request fails
    }
    sessionStorage.removeItem('admin_token');
    clearToken();
    setAdmin(null);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return (
    <AuthContext.Provider value={{
      admin,
      isAuthenticated: !!admin,
      isLoading,
      error,
      login,
      logout,
      clearError,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
